/** CP Profiles panel — create + list + validate profiles. */
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, User, Warning, Check } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { listCPProfiles, createCPProfile } from "@/lib/api";
import { CPField, CPTextInput, CPNumberInput, ChecklistRow } from "@/pages/wizard/cp/_cpShared";


export default function CPProfilesPanel({ projectId, summary, onRefresh }) {
  const qc = useQueryClient();
  const profilesQ = useQuery({
    queryKey: ["cp-profiles", projectId],
    queryFn:  () => listCPProfiles(projectId),
    enabled:  !!projectId,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    full_name: "", professional_title: "", qualification: "",
    professional_institution: "", membership_number: "", membership_grade: "",
    membership_status: "active", years_professional_exp: null, years_relevant_exp: null,
    is_independent: true, commodity_experience: [],
  });

  const create = useMutation({
    mutationFn: () => createCPProfile(projectId, form),
    onSuccess:  () => {
      toast.success("CP profile added");
      qc.invalidateQueries({ queryKey: ["cp-profiles", projectId] });
      onRefresh?.();
      setOpen(false);
      setForm({
        full_name: "", professional_title: "", qualification: "",
        professional_institution: "", membership_number: "", membership_grade: "",
        membership_status: "active", years_professional_exp: null, years_relevant_exp: null,
        is_independent: true, commodity_experience: [],
      });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message),
  });

  const rows = profilesQ.data?.profiles || [];
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">
          {rows.length} CP profile(s) on record · standard {summary?.reporting_standard || "JORC"}
        </div>
        <Button size="sm" onClick={() => setOpen(true)}
                    className="h-7 text-[11px] bg-brand-blue text-white"
                    data-testid="w-cp-profile-new">
          <Plus size={11} className="mr-1" /> Add CP profile
        </Button>
      </div>

      {rows.length === 0 && (
        <div className="border border-dashed border-slate-300 rounded p-6 text-center text-[12px] text-slate-500">
          No Competent Person profiles yet. Add at least one profile before drafting the declaration.
        </div>
      )}

      {rows.map((p) => (
        <div key={p.id} className="border border-slate-200 rounded p-3 bg-white"
              data-testid={`w-cp-profile-${p.id}`}>
          <div className="flex items-start gap-3">
            <User size={18} className="text-slate-500 mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <b className="text-[13px] text-slate-800">{p.full_name}</b>
                <span className="text-[10px] text-slate-500">{p.professional_title}</span>
                {p.is_independent
                  ? <span className="ml-auto px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-widest font-mono">Independent</span>
                  : <span className="ml-auto px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] uppercase tracking-widest font-mono">Non-Independent</span>}
              </div>
              <div className="text-[11px] text-slate-500 font-mono">
                {p.qualification || "—"} · {p.professional_institution || "—"} #{p.membership_number || "—"}
                {" · "}{p.years_relevant_exp ?? "—"} yrs relevant experience
              </div>
              {p.validation && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1 mt-2">
                  {Object.entries(p.validation.checks || {}).map(([k, v]) => (
                    <ChecklistRow key={k} ok={v} label={k.replaceAll("_", " ")} />
                  ))}
                </div>
              )}
              {p.validation && !p.validation.complete && (
                <div className="mt-2 text-[10px] italic text-amber-600 flex items-start gap-1">
                  <Warning size={11} className="mt-0.5" />
                  Profile is incomplete. Complete all checks before generating the declaration.
                </div>
              )}
              {p.validation?.complete && (
                <div className="mt-2 text-[10px] italic text-emerald-600 flex items-center gap-1">
                  <Check size={11} /> Profile passes the pre-declaration validation gate.
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Competent Person profile</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <CPField label="Full name" testId="w-cp-profile-name-lbl">
              <CPTextInput value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })}
                             placeholder="Dr. Alice Nyerere" testId="w-cp-profile-name" />
            </CPField>
            <CPField label="Professional title">
              <CPTextInput value={form.professional_title} onChange={(v) => setForm({ ...form, professional_title: v })}
                             placeholder="MAusIMM CP(Geo)" testId="w-cp-profile-title" />
            </CPField>
            <CPField label="Qualification" span={2}>
              <CPTextInput value={form.qualification} onChange={(v) => setForm({ ...form, qualification: v })}
                             placeholder="BSc(Hons) Geology, MSc Mineral Resources"
                             testId="w-cp-profile-qual" />
            </CPField>
            <CPField label="Professional institution">
              <CPTextInput value={form.professional_institution}
                             onChange={(v) => setForm({ ...form, professional_institution: v })}
                             placeholder="AusIMM" testId="w-cp-profile-inst" />
            </CPField>
            <CPField label="Membership number">
              <CPTextInput value={form.membership_number}
                             onChange={(v) => setForm({ ...form, membership_number: v })}
                             placeholder="12345" testId="w-cp-profile-memno" />
            </CPField>
            <CPField label="Membership grade">
              <CPTextInput value={form.membership_grade}
                             onChange={(v) => setForm({ ...form, membership_grade: v })}
                             placeholder="Chartered / Fellow" testId="w-cp-profile-grade" />
            </CPField>
            <CPField label="Membership status">
              <CPTextInput value={form.membership_status}
                             onChange={(v) => setForm({ ...form, membership_status: v })}
                             placeholder="active / expired" testId="w-cp-profile-status" />
            </CPField>
            <CPField label="Years professional" unit="yrs">
              <CPNumberInput value={form.years_professional_exp} step="0.5"
                                onChange={(v) => setForm({ ...form, years_professional_exp: v })}
                                testId="w-cp-profile-yp" />
            </CPField>
            <CPField label="Years relevant" unit="yrs">
              <CPNumberInput value={form.years_relevant_exp} step="0.5"
                                onChange={(v) => setForm({ ...form, years_relevant_exp: v })}
                                testId="w-cp-profile-yr" />
            </CPField>
            <div className="col-span-2 flex items-center gap-2 mt-2">
              <input type="checkbox" checked={form.is_independent}
                        onChange={(e) => setForm({ ...form, is_independent: e.target.checked })}
                        id="cp-indep" data-testid="w-cp-profile-indep" />
              <label htmlFor="cp-indep" className="text-[12px] font-mono text-slate-700">
                Independent Competent Person
              </label>
            </div>
          </div>
          <div className="flex justify-end mt-4 gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}
                       data-testid="w-cp-profile-cancel">Cancel</Button>
            <Button size="sm" className="bg-brand-blue text-white"
                       disabled={!form.full_name || create.isPending}
                       onClick={() => create.mutate()}
                       data-testid="w-cp-profile-save">
              {create.isPending ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
